import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Helper function to clear auth and redirect
const clearAuthAndRedirect = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
  localStorage.removeItem('assets');
  localStorage.removeItem('current_asset');
  localStorage.removeItem('organizations');
  localStorage.removeItem('current_organization');
  localStorage.removeItem('subscription_status');
  localStorage.removeItem('subscription_pending_plan');
  localStorage.removeItem('subscription_pending_period');

  // Only redirect if not already on login page to avoid loops
  if (!window.location.pathname.includes('/login')) {
    const here = window.location.pathname + window.location.search;
    window.location.href = `/login?session_expired=true${here && here !== '/' ? `&redirect=${encodeURIComponent(here)}` : ''}`;
  }
};

// Read a session token's payload. Tokens are signed JWTs (header.payload.sig,
// base64url); the server verifies the signature — this is only for expiry/UI.
export const decodeTokenPayload = (token: string): any | null => {
  try {
    const part = token.includes('.') ? token.split('.')[1] : token;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
  } catch {
    return null;
  }
};

// Check if token is expired (JWT tokens have expiry in payload)
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = decodeTokenPayload(token);
    if (!payload) return false;
    if (payload.exp) {
      return payload.exp * 1000 < Date.now();
    }
    return false;
  } catch {
    return false; // If we can't parse, let the server decide
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    const url = config.url || '';

    // Skip token check for auth endpoints
    const isPublicAuthEndpoint = url.includes('/auth/login') ||
                                  url.includes('/auth/signup') ||
                                  url.includes('/auth/verify') ||
                                  url.includes('/auth/invitation');

    if (token) {
      // Check if token is expired before making request
      if (!isPublicAuthEndpoint && isTokenExpired(token)) {
        console.log('Token expired - clearing auth');
        clearAuthAndRedirect();
        return Promise.reject(new Error('Token expired'));
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    // Remove Content-Type header for FormData requests
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';

    // Skip redirect for auth endpoints (login/signup/verify/invitation) - these are expected to fail
    // Also skip for verify-payment — this is a public endpoint, JWT may have expired during gateway redirect
    const isPublicAuthEndpoint = url.includes('/auth/login') ||
                                  url.includes('/auth/signup') ||
                                  url.includes('/auth/verify') ||
                                  url.includes('/auth/invitation') ||
                                  url.includes('/subscriptions/verify-payment');

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !isPublicAuthEndpoint) {
      console.log('401 Unauthorized - redirecting to login');
      clearAuthAndRedirect();
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // Send OTP for login
  sendLoginOTP: (email: string) => {
    const formData = new FormData();
    formData.append('email', email);
    return api.post('/v1/auth/login/send-otp', formData);
  },

  // Send OTP for signup
  sendSignupOTP: (email: string) => {
    const formData = new FormData();
    formData.append('email', email);
    return api.post('/v1/auth/signup/send-otp', formData);
  },

  // Verify OTP for login
  verifyLoginOTP: (email: string, otp: string, rememberMe = false) => {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('otp', otp);
    formData.append('remember_me', String(rememberMe));
    return api.post('/v1/auth/login/verify-otp', formData);
  },

  // Verify OTP for signup
  verifySignupOTP: (email: string, otp: string) => {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('otp', otp);
    return api.post('/v1/auth/signup/verify-otp', formData);
  },

  // Register user and organization
  register: (userData: any) => {
    const formData = new FormData();
    formData.append('name', userData.name);
    formData.append('email', userData.email);
    formData.append('country_code', userData.country_code || '');
    formData.append('mobile', userData.mobile || '');
    return api.post('/v1/auth/register', formData);
  },

  // Complete registration with all details (email, personal, organization)
  completeRegistration: (userData: any) => {
    const formData = new FormData();
    formData.append('email', userData.email);
    formData.append('name', userData.name);
    formData.append('country_code', userData.country_code || '');
    formData.append('mobile', userData.mobile || '');
    formData.append('organization_name', userData.organization_name);
    formData.append('organization_type', userData.organization_type);
    formData.append('industry', userData.industry);
    formData.append('website', userData.website || '');
    if (userData.plan_id) formData.append('plan_id', userData.plan_id.toString());
    if (userData.skip_trial) formData.append('skip_trial', '1');
    return api.post('/v1/auth/complete-registration', formData);
  },

  // Logout
  logout: () =>
    api.post('/auth/logout'),

  // Get invitation details
  getInvitation: (token: string) =>
    api.get(`/v1/auth/invitation/${token}`),

  // Accept invitation (for logged in users)
  acceptInvitation: (token: string) =>
    api.post(`/v1/auth/invitation/${token}/accept`),

  // Accept invitation directly — no prior login needed (existing users)
  acceptInvitationDirect: (token: string) =>
    api.post(`/v1/auth/invitation/${token}/accept-direct`),

  // Accept invitation and signup (for new users)
  acceptInvitationAndSignup: (token: string, userData: { name: string; country_code?: string; mobile?: string }) => {
    const formData = new FormData();
    formData.append('name', userData.name);
    if (userData.country_code) formData.append('country_code', userData.country_code);
    if (userData.mobile) formData.append('mobile', userData.mobile);
    return api.post(`/v1/auth/invitation/${token}/signup`, formData);
  },

  // OAuth
  getLoginMethods: () => api.get('/v1/auth/login-methods'),
  googleOAuth: (idToken: string) => api.post('/v1/auth/oauth/google', { id_token: idToken }),
  microsoftOAuth: (accessToken: string) => api.post('/v1/auth/oauth/microsoft', { access_token: accessToken }),
};

// Organization API
export const organizationAPI = {
  list: () =>
    api.get('/v1/organisations'),

  create: (orgData: any) =>
    api.post('/setup/organization', orgData),

  update: (id: number, orgData: any) => {
    return api.put(`/v1/organisations/${id}`, {
      name: orgData.name,
      website: orgData.website || '',
      industry: orgData.industry || '',
      size: orgData.size || '',
      address: orgData.address || '',
      phone: orgData.phone || '',
      country: orgData.country || '',
      currency: orgData.currency || '',
      date_format: orgData.date_format || '',
    });
  },

  get: (id: number) =>
    api.get(`/v1/organisations/${id}`),

  getCurrent: () =>
    api.get('/v1/organisations/current'),

  getMembers: (id: number) =>
    api.get(`/v1/organisations/${id}/members`),

  inviteMember: (id: number, memberData: any) => {
    const formData = new FormData();
    formData.append('email', memberData.email);
    formData.append('role', memberData.role);
    return api.post(`/v1/organisations/${id}/members`, formData);
  },

  removeMember: (orgId: number, userId: number) =>
    api.delete(`/v1/organisations/${orgId}/members/${userId}`),

  updateMemberRole: (orgId: number, userId: number, role: string) =>
    api.put(`/v1/organisations/${orgId}/members/${userId}/role`, { role }),
};

// Assets API
export const assetsAPI = {
  list: () =>
    api.get('/v1/assets'),

  getHealth: (assetId: number) =>
    api.get(`/v1/assets/${assetId}/health`),

  create: (assetData: any) =>
    api.post('/setup/asset', assetData),

  get: (id: number) =>
    api.get(`/v1/assets/${id}`),

  update: (id: number, assetData: any) =>
    api.put(`/v1/assets/${id}`, assetData),

  delete: (id: number) =>
    api.delete(`/v1/assets/${id}`),

  switchAsset: (assetId: number) =>
    api.post('/portal/switch-asset', { asset_id: assetId }),
};

// Setup API
export const setupAPI = {
  createOrganization: (orgData: any) => {
    const formData = new FormData();
    formData.append('name', orgData.name);
    formData.append('website', orgData.website || '');
    formData.append('industry', orgData.industry);
    formData.append('size', orgData.size);
    formData.append('country', orgData.country || '');
    formData.append('currency', orgData.currency || '');
    return api.post('/v1/setup/organization', formData);
  },

  createAsset: (assetData: any) => {
    const formData = new FormData();
    formData.append('name', assetData.name);
    formData.append('description', assetData.description || '');
    formData.append('platform_website', assetData.platform_website ? '1' : '0');
    formData.append('platform_mobile_app', assetData.platform_mobile_app ? '1' : '0');
    formData.append('platform_desktop_app', assetData.platform_desktop_app ? '1' : '0');
    formData.append('platform_iot', assetData.platform_iot ? '1' : '0');

    // Website URLs
    if (assetData.website_urls && assetData.website_urls.length > 0) {
      formData.append('website_urls', JSON.stringify(assetData.website_urls.filter((url: string) => url.trim())));
    }

    // Mobile app config
    if (assetData.mobile_app_config) {
      formData.append('mobile_app_config', JSON.stringify(assetData.mobile_app_config));
    }

    // Desktop app config
    if (assetData.desktop_app_config) {
      formData.append('desktop_app_config', JSON.stringify(assetData.desktop_app_config));
    }

    // IoT config
    if (assetData.iot_config) {
      formData.append('iot_config', JSON.stringify(assetData.iot_config));
    }

    return api.post('/v1/setup/asset', formData);
  },
};

// Analytics API
export const analyticsAPI = {
  getDashboardStats: (assetId: number, period: string = '7d') =>
    api.get(`/v1/analytics/dashboard/${assetId}?period=${period}`),

  getEnhancedSummary: (assetId: number, period: string = '30d', granularity: string = 'daily', dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams({ period, granularity });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return api.get(`/v1/analytics/enhanced-summary/${assetId}?${params.toString()}`);
  },

  getLiveVisitors: (assetId: number) =>
    api.get(`/v1/analytics/live/${assetId}`),

  getVisitorChart: (assetId: number, period: string = '7d') =>
    api.get(`/v1/analytics/visitors/${assetId}?period=${period}`),

  getTrafficSources: (assetId: number, period: string = '7d') =>
    api.get(`/v1/analytics/traffic-sources/${assetId}?period=${period}`),

  getTopPages: (assetId: number, period: string = '7d') =>
    api.get(`/v1/analytics/pageviews/${assetId}?period=${period}`),

  getPageDetail: (assetId: number, pageUrl: string, period: string = '7d') =>
    api.get(`/v1/analytics/pageviews/${assetId}/detail?url=${encodeURIComponent(pageUrl)}&period=${period}`),

  getDeviceStats: (assetId: number, period: string = '7d') =>
    api.get(`/v1/analytics/devices/${assetId}?period=${period}`),

  getEvents: (assetId: number, period: string = '7d') =>
    api.get(`/v1/analytics/events/${assetId}?period=${period}`),

  // Report endpoints with date range
  getReportData: (assetId: number, startDate: string, endDate: string) =>
    api.get(`/v1/analytics/report/${assetId}?start_date=${startDate}&end_date=${endDate}`),

  getReportPdf: (assetId: number, startDate: string, endDate: string) =>
    api.get(`/v1/analytics/pdf-report/${assetId}?start_date=${startDate}&end_date=${endDate}`, {
      responseType: 'blob'
    }),

  getGoalConversionStats: (assetId: number, period: string = '30d') =>
    api.get(`/v1/analytics/goal-conversion/${assetId}?period=${period}`),
};

// User API
export const userAPI = {
  getProfile: () =>
    api.get('/v1/user/profile'),

  updateProfile: (userData: any) => {
    return api.put('/v1/user/profile', {
      name: userData.name,
      country: userData.country || '',
      country_code: userData.country_code || '',
      mobile: userData.mobile || '',
    });
  },

  getUserAssets: () =>
    api.get('/v1/user/assets'),

  changePassword: (passwordData: any) => {
    const formData = new FormData();
    formData.append('current_password', passwordData.current_password);
    formData.append('new_password', passwordData.new_password);
    formData.append('confirm_password', passwordData.confirm_password);
    return api.post('/v1/user/change-password', formData);
  },
};

// Monitoring API
export const monitoringAPI = {
  // Get integration code snippets for various platforms
  getIntegrationCode: (assetId: number) => {
    const formData = new FormData();
    formData.append('asset_id', assetId.toString());
    return api.post('/v1/monitoring/integration-code', formData);
  },

  // Get SDK JavaScript URL
  getSdkUrl: (orgId: number, trackingId: string) => {
    return `${API_BASE_URL}/v1/integration/${orgId}/${trackingId}/cloudayn.js`;
  },

  // Track visitor/pageview/event (used by SDK)
  track: (trackingData: any) =>
    api.post('/v1/monitoring/track', trackingData),

  // Get health status for all monitored URLs
  getHealthStatus: (assetId: number) =>
    api.get(`/v1/monitoring/health/${assetId}`),

  // Get health check history/logs
  getHealthLog: (assetId: number, hours: number = 24) =>
    api.get(`/v1/monitoring/health-log/${assetId}?hours=${hours}`),

  // Get performance metrics history
  getPerformance: (assetId: number, days: number = 30) =>
    api.get(`/v1/monitoring/performance/${assetId}?days=${days}`),

  // Get downtime incidents
  getDowntimeIncidents: (assetId: number, days: number = 30, majorOnly: boolean = false) =>
    api.get(`/v1/monitoring/downtime/${assetId}?days=${days}${majorOnly ? '&major=true' : ''}`),

  // Get downtime summary by day
  getDowntimeSummary: (assetId: number, days: number = 30) =>
    api.get(`/v1/monitoring/downtime-summary/${assetId}?days=${days}`),

  // Get major downtimes (30+ minutes)
  getMajorDowntimes: (assetId: number, days: number = 90) =>
    api.get(`/v1/monitoring/major-downtimes/${assetId}?days=${days}`),

  // Endpoint management
  getEndpoints: (assetId: number) =>
    api.get(`/v1/monitoring/endpoints/${assetId}`),
  addEndpoint: (assetId: number, url: string) =>
    api.post(`/v1/monitoring/endpoints/${assetId}`, { url }),
  removeEndpoint: (assetId: number, url: string) =>
    api.delete(`/v1/monitoring/endpoints/${assetId}`, { data: { url } }),

  // Content scan history (PHP/IIS/language error detection + snapshot comparison)
  getContentScans: (assetId: number) =>
    api.get(`/v1/monitoring/content-scans/${assetId}`),

  // Manually trigger a content scan now (bypasses 4h interval)
  triggerContentScan: (assetId: number) =>
    api.post(`/v1/monitoring/content-scan-now/${assetId}`),

  // Content scan alert exemptions
  getContentExemptions: (assetId: number) =>
    api.get(`/v1/monitoring/content-exemptions/${assetId}`),
  addContentExemption: (assetId: number, url: string, reason?: string) =>
    api.post(`/v1/monitoring/content-exemptions/${assetId}`, { url, reason }),
  removeContentExemption: (assetId: number, url: string) =>
    api.delete(`/v1/monitoring/content-exemptions/${assetId}`, { data: { url } }),
};

// Security API
export const securityAPI = {
  // Trigger a manual security scan
  startScan: (assetId: number, options: { scan_type?: string; max_pages?: number } = {}) => {
    const formData = new FormData();
    formData.append('scan_type', options.scan_type || 'full');
    formData.append('max_pages', (options.max_pages || 50).toString());
    return api.post(`/v1/security/scan/${assetId}`, formData);
  },

  // Get status of a running or completed scan
  getScanStatus: (scanId: number) =>
    api.get(`/v1/security/scan-status/${scanId}`),

  // Get security issues/logs for an asset
  getLogs: (assetId: number, filters: { status?: string; severity?: string; days?: number } = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.days) params.append('days', filters.days.toString());
    return api.get(`/v1/security/logs/${assetId}?${params}`);
  },

  // Update security log status
  updateLog: (logId: number, data: { status: string; notes?: string }) => {
    const formData = new FormData();
    formData.append('status', data.status);
    if (data.notes) formData.append('notes', data.notes);
    return api.put(`/v1/security/log/${logId}`, formData);
  },

  // Get all malware/security signatures (Admin only)
  getSignatures: () =>
    api.get('/v1/security/signatures'),

  // Add new security signature (Admin only)
  addSignature: (signatureData: any) => {
    const formData = new FormData();
    formData.append('name', signatureData.name);
    formData.append('pattern', signatureData.pattern);
    formData.append('pattern_type', signatureData.pattern_type);
    formData.append('severity', signatureData.severity);
    if (signatureData.description) formData.append('description', signatureData.description);
    return api.post('/v1/security/signatures', formData);
  },

  // Get security overview/summary
  getOverview: (assetId: number) =>
    api.get(`/v1/security/overview/${assetId}`),

  // Run comprehensive vulnerability scan with optional crawling
  runVulnerabilityScan: (assetId: number, options: {
    url?: string;
    modules?: string[];
    crawl_depth?: number;
    max_pages?: number;
  } = {}) => {
    const formData = new FormData();
    if (options.url) formData.append('url', options.url);
    if (options.modules) formData.append('modules', options.modules.join(','));
    if (options.crawl_depth !== undefined) formData.append('crawl_depth', options.crawl_depth.toString());
    if (options.max_pages !== undefined) formData.append('max_pages', options.max_pages.toString());
    return api.post(`/v1/security/vulnerability-scan/${assetId}`, formData);
  },

  // Get available scan modules
  getScanModules: () =>
    api.get('/v1/security/scan-modules'),

  // Scan SSL/TLS configuration
  scanSsl: (assetId: number, url?: string) => {
    const formData = new FormData();
    if (url) formData.append('url', url);
    return api.post(`/v1/security/scan-ssl/${assetId}`, formData);
  },

  // Scan security headers
  scanHeaders: (assetId: number, url?: string) => {
    const formData = new FormData();
    if (url) formData.append('url', url);
    return api.post(`/v1/security/scan-headers/${assetId}`, formData);
  },

  // Fetch vulnerability data from third-party sources
  fetchVulnerabilities: (keyword: string, source: string = 'nvd') =>
    api.get(`/v1/security/vulnerabilities?keyword=${encodeURIComponent(keyword)}&source=${source}`),

  // Generate security report
  generateReport: (assetId: number) =>
    api.get(`/v1/security/report/${assetId}`),

  // Quick security check (no asset required)
  quickCheck: (url: string) => {
    const formData = new FormData();
    formData.append('url', url);
    return api.post('/v1/security/quick-check', formData);
  },

  // Run deep scan with URL crawling
  runDeepScan: (assetId: number, options: { url?: string; max_depth?: number; max_urls?: number } = {}) => {
    const formData = new FormData();
    if (options.url) formData.append('url', options.url);
    if (options.max_depth) formData.append('max_depth', options.max_depth.toString());
    if (options.max_urls) formData.append('max_urls', options.max_urls.toString());
    return api.post(`/v1/security/deep-scan/${assetId}`, formData);
  },

  // Scan ports
  scanPorts: (assetId: number, url?: string) => {
    const formData = new FormData();
    if (url) formData.append('url', url);
    return api.post(`/v1/security/scan-ports/${assetId}`, formData);
  },

  // Check best practices
  checkBestPractices: (assetId: number, url?: string) => {
    const formData = new FormData();
    if (url) formData.append('url', url);
    return api.post(`/v1/security/check-best-practices/${assetId}`, formData);
  },

  // Generate PDF report (returns blob for download)
  generatePdfReport: (assetId: number) =>
    api.get(`/v1/security/pdf-report/${assetId}`, { responseType: 'blob' }),

  // Get scan detail
  getScanDetail: (scanId: number) =>
    api.get(`/v1/security/scan-detail/${scanId}`),

  // Get issue detail
  getIssueDetail: (issueId: number) =>
    api.get(`/v1/security/issue/${issueId}`),

  // Get full comprehensive report
  getFullReport: (assetId: number) =>
    api.get(`/v1/security/full-report/${assetId}`),

  // Crawl and list all URLs
  crawlUrls: (assetId: number, options: { url?: string; max_depth?: number; max_urls?: number } = {}) => {
    const formData = new FormData();
    if (options.url) formData.append('url', options.url);
    if (options.max_depth) formData.append('max_depth', options.max_depth.toString());
    if (options.max_urls) formData.append('max_urls', options.max_urls.toString());
    return api.post(`/v1/security/crawl-urls/${assetId}`, formData);
  },

  // Get issues organized by page URL
  getIssuesByPage: (assetId: number) =>
    api.get(`/v1/security/issues-by-page/${assetId}`),

  // Get form security issues
  getFormSecurityIssues: (assetId: number) =>
    api.get(`/v1/security/form-issues/${assetId}`),

  // Get crawled pages
  getCrawledPages: (assetId: number) =>
    api.get(`/v1/security/crawled-pages/${assetId}`),

  // Notification preferences (daily / weekly / monthly security digest)
  getNotificationPreferences: (assetId: number) =>
    api.get(`/v1/security/preferences/${assetId}`),

  setNotificationPreferences: (
    assetId: number,
    prefs: { daily_enabled: boolean; weekly_enabled: boolean; monthly_enabled: boolean }
  ) => api.put(`/v1/security/preferences/${assetId}`, prefs),
};

// Endpoint / IT asset management API
export const endpointAPI = {
  // List endpoints (optionally filter by asset / status)
  list: (params: { asset_id?: number; status?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.asset_id) qs.append('asset_id', String(params.asset_id));
    if (params.status) qs.append('status', params.status);
    return api.get(`/v1/endpoints?${qs.toString()}`);
  },

  // Enroll a new endpoint by name -> returns install commands + token
  create: (data: { asset_id: number; name: string; os?: string }) =>
    api.post('/v1/endpoints', data),

  // Endpoint detail (inventory + latest metrics)
  get: (id: number) => api.get(`/v1/endpoints/${id}`),

  // Metrics history for charts / live view
  getMetrics: (id: number, hours = 24) =>
    api.get(`/v1/endpoints/${id}/metrics?hours=${hours}`),

  // asset_id moves the device to another asset (group) in the same organisation
  update: (id: number, data: { name?: string; is_active?: boolean; asset_id?: number }) =>
    api.put(`/v1/endpoints/${id}`, data),

  remove: (id: number) => api.delete(`/v1/endpoints/${id}`),

  // Remote commands
  issueCommand: (id: number, command_type: string, payload?: any) =>
    api.post(`/v1/endpoints/${id}/commands`, { command_type, payload }),

  listCommands: (id: number) => api.get(`/v1/endpoints/${id}/commands`),

  // Fleet-wide command
  issueFleetCommand: (asset_id: number, command_type: string, payload?: any) =>
    api.post('/v1/endpoints/fleet/command', { asset_id, command_type, payload }),

  // Phase 2: scans & threats
  listScans: (id: number) => api.get(`/v1/endpoints/${id}/scans`),
  listThreats: (id: number, status?: string) =>
    api.get(`/v1/endpoints/${id}/threats${status ? `?status=${status}` : ''}`),
  updateThreat: (id: number, threatId: number, data: { status?: string; action?: string }) =>
    api.put(`/v1/endpoints/${id}/threats/${threatId}`, data),

  // Phase 3: usage policy & telemetry
  getPolicy: () => api.get('/v1/endpoints/policy'),
  setPolicy: (data: Record<string, boolean | string>) => api.put('/v1/endpoints/policy', data),
  getUsage: (id: number, days = 7) => api.get(`/v1/endpoints/${id}/usage?days=${days}`),

  // Central threat feed (org-wide)
  threatFeed: (status = 'open') => api.get(`/v1/endpoints/threats/feed?status=${status}`),
};

// AI Security API
export const aiSecurityAPI = {
  // Get AI security overview
  getOverview: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/overview`),

  // Get page changes
  getChanges: (assetId: number, filters?: { reviewed?: boolean; suspicious_only?: boolean; days?: number }) => {
    const params = new URLSearchParams();
    if (filters?.reviewed !== undefined) params.append('reviewed', filters.reviewed.toString());
    if (filters?.suspicious_only) params.append('suspicious_only', 'true');
    if (filters?.days) params.append('days', filters.days.toString());
    return api.get(`/v1/ai-security/${assetId}/changes?${params}`);
  },

  // Get change details
  getChangeDetail: (assetId: number, changeId: number) =>
    api.get(`/v1/ai-security/${assetId}/changes/${changeId}`),

  // Mark change as reviewed
  reviewChange: (assetId: number, changeId: number, notes?: string) => {
    const data: { notes?: string } = {};
    if (notes) data.notes = notes;
    return api.post(`/v1/ai-security/${assetId}/changes/${changeId}/review`, data);
  },

  // Get malware detections
  getMalwareDetections: (assetId: number, filters?: { status?: string; severity?: string; days?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.days) params.append('days', filters.days.toString());
    return api.get(`/v1/ai-security/${assetId}/malware?${params}`);
  },

  // Trigger page crawl
  triggerCrawl: (assetId: number) =>
    api.post(`/v1/ai-security/${assetId}/crawl`),

  // Trigger full AI security scan
  triggerScan: (assetId: number) =>
    api.post(`/v1/ai-security/${assetId}/scan`),

  // Get scan progress
  getScanProgress: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/scan-progress`),

  // Trigger malware scan only
  triggerMalwareScan: (assetId: number) =>
    api.post(`/v1/ai-security/${assetId}/malware/scan`),

  // Resolve malware detection
  resolveMalware: (assetId: number, detectionId: number, notes?: string) => {
    const data: { notes?: string } = {};
    if (notes) data.notes = notes;
    return api.post(`/v1/ai-security/${assetId}/malware/${detectionId}/resolve`, data);
  },

  // Mark as false positive
  markFalsePositive: (assetId: number, detectionId: number, reason?: string) => {
    const data: { reason?: string } = {};
    if (reason) data.reason = reason;
    return api.post(`/v1/ai-security/${assetId}/malware/${detectionId}/false-positive`, data);
  },

  // Get AI suggestions
  getSuggestions: (assetId: number, filters?: { severity?: string; dismissed?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.dismissed !== undefined) params.append('dismissed', filters.dismissed.toString());
    return api.get(`/v1/ai-security/${assetId}/suggestions?${params}`);
  },

  // Dismiss suggestion
  dismissSuggestion: (assetId: number, suggestionId: number, reason?: string) => {
    const data: { reason?: string } = {};
    if (reason) data.reason = reason;
    return api.post(`/v1/ai-security/${assetId}/suggestions/${suggestionId}/dismiss`, data);
  },

  // Mark suggestion as applied
  applySuggestion: (assetId: number, suggestionId: number) =>
    api.post(`/v1/ai-security/${assetId}/suggestions/${suggestionId}/applied`),

  // Get latest audit score
  getAuditScore: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/audit-score`),

  // Get audit score history
  getAuditHistory: (assetId: number, days: number = 30) =>
    api.get(`/v1/ai-security/${assetId}/audit-history?days=${days}`),

  // Get monitored pages with security status
  getMonitoredPages: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/monitored-pages`),

  // Get endpoint info (IP addresses)
  getEndpointInfo: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/endpoint-info`),

  // Get scan history (actual scan records)
  getScanHistory: (assetId: number, limit: number = 20) =>
    api.get(`/v1/ai-security/${assetId}/scan-history?limit=${limit}`),

  // Get traffic analysis (DDoS/spike detection)
  getTrafficAnalysis: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/traffic-analysis`),

  // Get network scan results (open ports, provider info)
  getNetworkScan: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/network-scan`),

  // Get WordPress scan results
  getWordPressScan: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/wordpress-scan`),

  // Get exposed sensitive files
  getSensitiveFiles: (assetId: number) =>
    api.get(`/v1/ai-security/${assetId}/sensitive-files`),

  // Trigger quick sensitive file scan
  triggerSensitiveFileScan: (assetId: number) =>
    api.post(`/v1/ai-security/${assetId}/sensitive-files/scan`),

  // Admin: Get malware signatures
  getSignatures: () =>
    api.get('/v1/ai-security/admin/signatures'),

  // Admin: Create malware signature
  createSignature: (signatureData: {
    name: string;
    pattern: string;
    pattern_type: 'regex' | 'contains' | 'exact';
    severity: 'low' | 'medium' | 'high' | 'critical';
    description?: string;
    category?: string;
  }) => api.post('/v1/ai-security/admin/signatures', signatureData),

  // Admin: Update malware signature
  updateSignature: (signatureId: number, signatureData: any) =>
    api.put(`/v1/ai-security/admin/signatures/${signatureId}`, signatureData),

  // Admin: Delete malware signature
  deleteSignature: (signatureId: number) =>
    api.delete(`/v1/ai-security/admin/signatures/${signatureId}`),
};

// Cron Job Management API (Admin only)
export const cronAPI = {
  // List all cron jobs
  getJobs: () =>
    api.get('/v1/cron/jobs'),

  // Manually trigger a cron job
  runJob: (jobId: number) =>
    api.post(`/v1/cron/jobs/${jobId}/run`),

  // Get execution logs for a cron job
  getJobLogs: (jobId: number, limit: number = 50) =>
    api.get(`/v1/cron/logs/${jobId}?limit=${limit}`),

  // Toggle job active status
  toggleJob: (jobId: number, isActive: boolean) => {
    const formData = new FormData();
    formData.append('is_active', isActive ? '1' : '0');
    return api.put(`/v1/cron/jobs/${jobId}`, formData);
  },
};

// Server Monitoring API
export const serverAPI = {
  // List all servers for the user
  list: (filters?: { status?: string; asset_id?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.asset_id) params.append('asset_id', filters.asset_id.toString());
    const queryString = params.toString();
    return api.get(`/v1/servers${queryString ? `?${queryString}` : ''}`);
  },

  // Register a new server
  create: (serverData: {
    asset_id: number;
    name: string;
    server_type: 'virtual' | 'physical';
    os: 'linux' | 'windows' | 'macos' | 'freebsd' | 'other';
    os_version?: string;
    public_ip?: string;
    private_ip?: string;
    hostname?: string;
    web_server_type?: 'apache' | 'nginx' | 'tomcat' | 'nodejs' | 'iis' | 'caddy' | 'lighttpd' | 'other';
    app_root_path?: string;
  }) => api.post('/v1/servers', serverData),

  // Get server details
  get: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}`),

  // Update server
  update: (serverId: number | string, serverData: any) =>
    api.put(`/v1/servers/${serverId}`, serverData),

  // Delete server
  delete: (serverId: number | string) =>
    api.delete(`/v1/servers/${serverId}`),

  // Get servers by asset
  getByAsset: (assetId: number) =>
    api.get(`/v1/assets/${assetId}/servers`),

  // Get current server status with latest metrics
  getStatus: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}/status`),

  // Get historical metrics
  getMetrics: (serverId: number | string, period: '1h' | '6h' | '24h' | '7d' | '30d' = '24h', metric?: 'cpu' | 'memory' | 'disk' | 'network') => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (metric) params.append('metric', metric);
    return api.get(`/v1/servers/${serverId}/metrics?${params}`);
  },

  // Get server events/logs
  getEvents: (serverId: number | string, filters?: {
    type?: 'ssh_login' | 'ssh_failed' | 'web_access' | 'web_error' | 'app_log' | 'system' | 'security';
    severity?: 'debug' | 'info' | 'warning' | 'error' | 'critical';
    days?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.days) params.append('days', filters.days.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    return api.get(`/v1/servers/${serverId}/events?${params}`);
  },

  // Get full server dashboard data
  getDashboard: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}/dashboard`),

  // Get servers overview for an asset
  getOverview: (assetId: number) =>
    api.get(`/v1/assets/${assetId}/servers/overview`),

  // Generate install script
  getInstallScript: (serverId: number | string) =>
    api.post(`/v1/servers/${serverId}/install-script`),

  // Check port health (TCP) for a server
  checkPorts: (serverId: number | string, ports?: number[]) => {
    const query = ports?.length ? `?ports=${ports.join(',')}` : '';
    return api.get(`/v1/servers/${serverId}/ports${query}`);
  },

  // Issue a remote command to the server agent
  issueCommand: (serverId: number | string, commandType: string, commandPayload?: any) =>
    api.post(`/v1/servers/${serverId}/remote/command`, { command_type: commandType, command_payload: commandPayload }),

  // List remote command history
  listCommands: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}/remote/commands`),

  // Get latest system info for a server
  getSysInfo: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}/sysinfo`),

  // List DB connections for a server
  listDBConnections: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}/db-connections`),

  // Add a DB connection to a server
  createDBConnection: (serverId: number | string, data: any) =>
    api.post(`/v1/servers/${serverId}/db-connections`, data),

  // Delete a DB connection
  deleteDBConnection: (serverId: number | string, connId: number | string) =>
    api.delete(`/v1/servers/${serverId}/db-connections/${connId}`),

  // Get DB metrics for a connection (includes latest live/slow queries)
  getDBMetrics: (serverId: number | string, connId: number | string) =>
    api.get(`/v1/servers/${serverId}/db-connections/${connId}/metrics`),

  // Get latest agent release info
  getLatestAgentRelease: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}/agent-release/latest`),

  // WebSocket connection status
  getWsStatus: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}/ws-status`),

  // Application management
  getApps: (serverId: number | string) =>
    api.get(`/v1/servers/${serverId}/apps`),

  restartApp: (serverId: number | string, app: string) =>
    api.post(`/v1/servers/${serverId}/apps/${app}/restart`),

  getAppLogs: (serverId: number | string, app: string, lines = 200) =>
    api.get(`/v1/servers/${serverId}/apps/${app}/logs`, { params: { lines } }),

  getAppConfig: (serverId: number | string, app: string, file?: string) =>
    api.get(`/v1/servers/${serverId}/apps/${app}/config`, { params: file ? { file } : undefined }),

  setAppConfig: (serverId: number | string, app: string, content: string, file?: string) =>
    api.put(`/v1/servers/${serverId}/apps/${app}/config`, { content, file }),

  // File manager
  saveFile: (serverId: number | string, path: string, content: string) =>
    api.put(`/v1/servers/${serverId}/files`, { path, content }),

  listFileEdits: (serverId: number | string, path: string) =>
    api.get(`/v1/servers/${serverId}/file-edits`, { params: { path } }),

  getFileEditContent: (serverId: number | string, editId: number) =>
    api.get(`/v1/servers/${serverId}/file-edits/${editId}`),
};

// Event Types API
export const eventTypesAPI = {
  // List all event types
  list: () =>
    api.get('/v1/event-types'),

  // Get summary of all event types with stats
  getSummary: (period: string = '30d', assetId?: number) => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (assetId) params.append('asset_id', assetId.toString());
    return api.get(`/v1/event-types/summary?${params}`);
  },

  // Get a single event type
  get: (id: number) =>
    api.get(`/v1/event-types/${id}`),

  // Create a new event type
  create: (eventTypeData: {
    name: string;
    display_name: string;
    category?: string;
    description?: string;
    has_value?: boolean;
    value_type?: 'number' | 'currency' | 'percentage';
    value_label?: string;
    color?: string;
    icon?: string;
    trigger_type?: 'frontend' | 'backend' | 'both';
    require_fingerprint?: boolean;
    require_user_id?: boolean;
    is_goal?: boolean;
  }) => api.post('/v1/event-types', eventTypeData),

  // Update an event type
  update: (id: number, eventTypeData: any) =>
    api.put(`/v1/event-types/${id}`, eventTypeData),

  // Delete an event type
  delete: (id: number) =>
    api.delete(`/v1/event-types/${id}`),

  // Get event type statistics
  getStats: (id: number, period: string = '30d', assetId?: number) => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (assetId) params.append('asset_id', assetId.toString());
    return api.get(`/v1/event-types/${id}/stats?${params}`);
  },

  // Get events for an event type
  getEvents: (id: number, page: number = 1, limit: number = 50, assetId?: number) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (assetId) params.append('asset_id', assetId.toString());
    return api.get(`/v1/event-types/${id}/events?${params}`);
  },

  // Get code snippets for an event type
  getCodeSnippets: (id: number) =>
    api.get(`/v1/event-types/${id}/snippets`),
};

// Alerts API
export const alertsAPI = {
  // List all alerts
  list: (filters?: {
    status?: 'active' | 'acknowledged' | 'resolved' | 'silenced';
    severity?: 'info' | 'warning' | 'error' | 'critical';
    source_type?: 'server' | 'website_health' | 'security_scan' | 'performance';
    days?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.source_type) params.append('source_type', filters.source_type);
    if (filters?.days) params.append('days', filters.days.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    return api.get(`/v1/alerts?${params}`);
  },

  // Get alert details
  get: (alertId: number) =>
    api.get(`/v1/alerts/${alertId}`),

  // Get alerts for an asset
  getByAsset: (assetId: number, filters?: { status?: string; severity?: string; days?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.days) params.append('days', filters.days.toString());
    return api.get(`/v1/assets/${assetId}/alerts?${params}`);
  },

  // Acknowledge an alert
  acknowledge: (alertId: number, notes?: string) => {
    const data: { notes?: string } = {};
    if (notes) data.notes = notes;
    return api.put(`/v1/alerts/${alertId}/acknowledge`, data);
  },

  // Resolve an alert
  resolve: (alertId: number, resolution_notes?: string) => {
    const data: { resolution_notes?: string } = {};
    if (resolution_notes) data.resolution_notes = resolution_notes;
    return api.put(`/v1/alerts/${alertId}/resolve`, data);
  },

  // Silence an alert
  silence: (alertId: number, duration_hours: number, reason?: string) => {
    const data: { duration_hours: number; reason?: string } = { duration_hours };
    if (reason) data.reason = reason;
    return api.put(`/v1/alerts/${alertId}/silence`, data);
  },

  // Get alert statistics
  getStats: (days: number = 30) =>
    api.get(`/v1/alerts/stats?days=${days}`),

  // Send notification for an alert
  sendNotification: (alertId: number) =>
    api.post(`/v1/alerts/${alertId}/send-notification`),

  // Get email history
  getEmailHistory: (filters?: {
    status?: string;
    email_type?: string;
    days?: number;
    limit?: number;
    offset?: number;
    search?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.email_type) params.append('email_type', filters.email_type);
    if (filters?.days) params.append('days', filters.days.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    if (filters?.search) params.append('search', filters.search);
    return api.get(`/v1/alerts/email-history?${params}`);
  },

  // Get email history record
  getEmailHistoryRecord: (historyId: number) =>
    api.get(`/v1/alerts/email-history/${historyId}`),
};

// Subscription & Billing API
export const subscriptionAPI = {
  listPlans: () =>
    api.get('/v1/subscriptions/plans'),

  getCurrent: () =>
    api.get('/v1/subscriptions/current'),

  subscribe: (data: { plan_id: number; billing_period: string; gateway: string; payment_token: string; discount_code?: string }) =>
    api.post('/v1/subscriptions/subscribe', data),

  previewFees: (data: { plan_id: number; billing_period: string; discount_code?: string }) =>
    api.post('/v1/subscriptions/preview-fees', data),

  getPlanLimits: () =>
    api.get('/v1/subscriptions/plan-limits'),

  changePlan: (newPlanId: number) =>
    api.post('/v1/subscriptions/change-plan', { new_plan_id: newPlanId }),

  cancel: (reason: string) =>
    api.post('/v1/subscriptions/cancel', { reason }),

  listPayments: (page: number = 1, limit: number = 20) =>
    api.get(`/v1/subscriptions/payments?page=${page}&limit=${limit}`),

  getReceipt: (paymentId: number) =>
    api.get(`/v1/subscriptions/payments/${paymentId}/receipt`),

  addPaymentMethod: (data: { gateway: string; token: string; type?: string; brand?: string; last_four?: string; exp_month?: number; exp_year?: number; holder_name?: string }) =>
    api.post('/v1/subscriptions/payment-methods', data),

  removePaymentMethod: (id: number) =>
    api.delete(`/v1/subscriptions/payment-methods/${id}`),

  setDefaultPaymentMethod: (id: number) =>
    api.put(`/v1/subscriptions/payment-methods/${id}/default`),

  startTrial: (planId: number, billingPeriod: string = 'monthly') =>
    api.post('/v1/subscriptions/start-trial', { plan_id: planId, billing_period: billingPeriod }),

  verifyPayment: (tapId: string) =>
    api.get(`/v1/subscriptions/verify-payment?tap_id=${encodeURIComponent(tapId)}`),
};

// Discount API
export const discountAPI = {
  validate: (code: string, planId: number, billingPeriod: string) =>
    api.post('/v1/discounts/validate', { code, plan_id: planId, billing_period: billingPeriod }),
};

// Email Monitor API
export const emailMonitorAPI = {
  getDashboard: (assetId: number) =>
    api.get(`/v1/email-monitor/dashboard/${assetId}`),

  getDnsRecords: (assetId: number) =>
    api.get(`/v1/email-monitor/dns-records/${assetId}`),

  getDnsHistory: (assetId: number, params?: { record_type?: string; days?: number }) =>
    api.get(`/v1/email-monitor/dns-history/${assetId}`, { params }),

  getBlacklistStatus: (assetId: number) =>
    api.get(`/v1/email-monitor/blacklist/${assetId}`),

  triggerScan: (assetId: number) =>
    api.post(`/v1/email-monitor/scan/${assetId}`),

  getOpenIssues: (assetId: number) =>
    api.get(`/v1/email-monitor/issues/${assetId}`),

  hasIssues: () =>
    api.get('/v1/email-monitor/has-issues'),
};

// Widget API
export const widgetAPI = {
  getSettings: (assetId?: number) =>
    api.get('/v1/widgets/settings', { params: assetId ? { asset_id: assetId } : {} }),

  updateSettings: (data: {
    asset_id?: number;
    widget_score?: boolean;
    widget_status?: boolean;
    widget_online?: boolean;
    widget_metrics?: boolean;
    theme?: string;
    size?: string;
    is_active?: boolean;
  }) => api.put('/v1/widgets/settings', data),

  regenerateKey: (assetId?: number) =>
    api.post('/v1/widgets/regenerate-key', { asset_id: assetId }),
};

export const campaignAPI = {
  list: (assetId?: number) =>
    api.get(`/v1/campaigns${assetId ? `?asset_id=${assetId}` : ''}`),
  create: (data: { name: string; asset_id: number; description?: string; target_url?: string }) =>
    api.post('/v1/campaigns', data),
  update: (id: number, data: any) =>
    api.put(`/v1/campaigns/${id}`, data),
  delete: (id: number) =>
    api.delete(`/v1/campaigns/${id}`),
  getAnalytics: (id: number, days = 30) =>
    api.get(`/v1/campaigns/${id}/analytics?days=${days}`),
};

export const visitorLogAPI = {
  list: (assetId: number, page = 1, limit = 50, search = '', dateFrom = '', dateTo = '') => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit), search });
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return api.get(`/v1/analytics/visitor-log/${assetId}?${params.toString()}`);
  },
  getToday: (assetId: number, page = 1, limit = 50) =>
    api.get(`/v1/analytics/visitor-log/${assetId}/today?page=${page}&limit=${limit}`),
  getDetail: (assetId: number, visitorId: string) =>
    api.get(`/v1/analytics/visitor-log/${assetId}/${encodeURIComponent(visitorId)}`),
};

// SEO API
export const seoAPI = {
  getPages: (assetId: number) =>
    api.get(`/v1/seo/pages/${assetId}`),

  getScans: (assetId: number) =>
    api.get(`/v1/seo/scans/${assetId}`),

  startScan: (assetId: number) =>
    api.post(`/v1/seo/scan/${assetId}`),

  getScanStatus: (scanId: number) =>
    api.get(`/v1/seo/scan/${scanId}/status`),

  getResults: (assetId: number) =>
    api.get(`/v1/seo/results/${assetId}`),

  getResult: (assetId: number, id: number) =>
    api.get(`/v1/seo/results/${assetId}/${id}`),

  generateAI: (resultId: number) =>
    api.post(`/v1/seo/ai/${resultId}`),

  getKeywords: (assetId: number) =>
    api.get(`/v1/seo/keywords/${assetId}`),

  saveKeywords: (assetId: number, keywords: string[]) =>
    api.post(`/v1/seo/keywords/${assetId}`, { keywords }),

  autoExtractKeywords: (assetId: number) =>
    api.post(`/v1/seo/keywords/${assetId}/auto`),

  checkRanks: (assetId: number) =>
    api.post(`/v1/seo/keywords/check-ranks/${assetId}`),

  getRanks: (assetId: number) =>
    api.get(`/v1/seo/keywords/ranks/${assetId}`),

  getTrackedPages: (assetId: number) =>
    api.get(`/v1/seo/tracked-pages/${assetId}`),

  removeTrackedPage: (assetId: number, pageId: number) =>
    api.delete(`/v1/seo/tracked-pages/${assetId}/${pageId}`),

  restoreTrackedPage: (assetId: number, pageId: number) =>
    api.patch(`/v1/seo/tracked-pages/${assetId}/${pageId}/restore`),

  refreshTrackedPages: (assetId: number) =>
    api.post(`/v1/seo/tracked-pages/${assetId}/refresh`),

  getPreferences: (assetId: number) =>
    api.get(`/v1/seo/preferences/${assetId}`),

  setPreferences: (assetId: number, prefs: { daily_enabled: boolean; weekly_enabled: boolean }) =>
    api.put(`/v1/seo/preferences/${assetId}`, prefs),

  // SEO for AI
  getAITagsResult: (assetId: number) =>
    api.get(`/v1/seo/ai-tags/${assetId}`),
  scanAITags: (assetId: number) =>
    api.post(`/v1/seo/ai-tags/scan/${assetId}`),
  generateAITag: (assetId: number, tagKey: string) =>
    api.post(`/v1/seo/ai-tags/generate/${assetId}`, { tag_key: tagKey }),

  // Backlinks
  getBacklinks: (assetId: number) =>
    api.get(`/v1/seo/backlinks/${assetId}`),
  checkBacklinks: (assetId: number) =>
    api.post(`/v1/seo/backlinks/check/${assetId}`),
};

export const codeScanAPI = {
  getDashboard: (params?: any) =>
    api.get('/v1/code-scan/dashboard', { params }),
  listTargets: (params?: any) =>
    api.get('/v1/code-scan', { params }),
  createTarget: (data: { server_id: number; path: string; label?: string; schedule?: string }) =>
    api.post('/v1/code-scan', data),
  updateTarget: (id: number, data: { label?: string; path?: string; is_enabled?: number; schedule?: string }) =>
    api.put(`/v1/code-scan/${id}`, data),
  deleteTarget: (id: number) =>
    api.delete(`/v1/code-scan/${id}`),
  listResults: (params?: any) =>
    api.get('/v1/code-scan/results', { params }),
  getResult: (id: number) =>
    api.get(`/v1/code-scan/results/${id}`),
  getVulnerabilities: (id: number, params?: any) =>
    api.get(`/v1/code-scan/results/${id}/vulnerabilities`, { params }),
  triggerScan: (serverId: number) =>
    api.post('/v1/code-scan/trigger', { server_id: serverId }),
};

export const feedbackAPI = {
  getConfig: (assetId: number) =>
    api.get('/v1/feedback/config', { params: { asset_id: assetId } }),
  updateConfig: (data: any) =>
    api.put('/v1/feedback/config', data),
  list: (params: any) =>
    api.get('/v1/feedback', { params }),
  getStats: (assetId: number) =>
    api.get('/v1/feedback/stats', { params: { asset_id: assetId } }),
  updateStatus: (id: number, status: string) =>
    api.put(`/v1/feedback/${id}/status`, { status }),
  deleteFeedback: (id: number) =>
    api.delete(`/v1/feedback/${id}`),
  export: (params: any) =>
    api.get('/v1/feedback/export', { params, responseType: 'blob' }),
};

export const savedCommandsAPI = {
  list: () =>
    api.get('/v1/saved-commands'),
  create: (data: { name: string; description?: string; command_type: string; command_payload?: any }) =>
    api.post('/v1/saved-commands', data),
  update: (id: number, data: { name?: string; description?: string; command_payload?: any }) =>
    api.put(`/v1/saved-commands/${id}`, data),
  delete: (id: number) =>
    api.delete(`/v1/saved-commands/${id}`),
  execute: (id: number, serverId: number) =>
    api.post(`/v1/saved-commands/${id}/execute`, { server_id: serverId }),
};

// WebSocket helper — creates a reconnecting WS connection to a server
export const createServerWs = (serverId: number): WebSocket | null => {
  const token = localStorage.getItem('auth_token');
  if (!token) return null;
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const wsBase = apiBase
    .replace('https://', 'wss://')
    .replace('http://', 'ws://')
    .replace(/\/api$/, '');
  const wsUrl = `${wsBase}/api/v1/ws/servers/${serverId}?token=${encodeURIComponent(token)}`;
  return new WebSocket(wsUrl);
};

// Live shell WebSocket for an endpoint (Phase 4).
export const createEndpointWs = (endpointId: number): WebSocket | null => {
  const token = localStorage.getItem('auth_token');
  if (!token) return null;
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const wsBase = apiBase
    .replace('https://', 'wss://')
    .replace('http://', 'ws://')
    .replace(/\/api$/, '');
  const wsUrl = `${wsBase}/api/v1/ws/endpoints/${endpointId}?token=${encodeURIComponent(token)}`;
  return new WebSocket(wsUrl);
};

// Per-asset email notification settings + recipients (one place for all categories)
export const notificationSettingsAPI = {
  get: (assetId: number) => api.get(`/v1/notification-settings/${assetId}`),
  updateCategory: (assetId: number, category: string, settings: Record<string, boolean>) =>
    api.put(`/v1/notification-settings/${assetId}/${category}`, settings),
  updateRecipient: (assetId: number, userId: number, enabled: boolean) =>
    api.put(`/v1/notification-settings/${assetId}/recipients/${userId}`, { enabled }),
};

// Guided (re-)onboarding checklist per asset + home-dashboard prompt
export const onboardingAPI = {
  overview: (organisationId?: number) =>
    api.get('/v1/onboarding/overview', { params: organisationId ? { organisation_id: organisationId } : {} }),
  prompt: (organisationId?: number) =>
    api.get('/v1/onboarding/prompt', { params: organisationId ? { organisation_id: organisationId } : {} }),
  dismissPrompt: (organisationId?: number) => api.post('/v1/onboarding/prompt/dismiss', { organisation_id: organisationId }),
  markOpened: (organisationId?: number) => api.post('/v1/onboarding/prompt/opened', { organisation_id: organisationId }),
  get: (assetId: number) => api.get(`/v1/onboarding/assets/${assetId}`),
  start: (assetId: number) => api.post(`/v1/onboarding/assets/${assetId}/start`),
  complete: (assetId: number) => api.post(`/v1/onboarding/assets/${assetId}/complete`),
  restart: (assetId: number) => api.post(`/v1/onboarding/assets/${assetId}/restart`),
  snooze: (assetId: number) => api.post(`/v1/onboarding/assets/${assetId}/snooze`),
  skipStep: (assetId: number, step: string, reason: string) =>
    api.post(`/v1/onboarding/assets/${assetId}/steps/${step}/skip`, { reason }),
  reviewStep: (assetId: number, step: string) => api.post(`/v1/onboarding/assets/${assetId}/steps/${step}/review`),
  resetStep: (assetId: number, step: string) => api.delete(`/v1/onboarding/assets/${assetId}/steps/${step}`),
};


// AI features (Claude): security audits, alert explanations, help assistant
export const aiAPI = {
  status: (assetId: number) => api.get(`/v1/ai/${assetId}/status`),
  generateAudit: (assetId: number) => api.post(`/v1/ai/${assetId}/audits`),
  listAudits: (assetId: number) => api.get(`/v1/ai/${assetId}/audits`),
  latestAudit: (assetId: number) => api.get(`/v1/ai/${assetId}/audits/latest`),
  getAudit: (assetId: number, reportId: number) => api.get(`/v1/ai/${assetId}/audits/${reportId}`),
  explainAlert: (assetId: number, body: { kind?: string; url?: string; details?: any; alert_id?: number }) =>
    api.post(`/v1/ai/${assetId}/insights`, body),
  conversations: (assetId: number) => api.get(`/v1/ai/${assetId}/assistant/conversations`),
  conversation: (assetId: number, id: number) => api.get(`/v1/ai/${assetId}/assistant/conversations/${id}`),
  deleteConversation: (assetId: number, id: number) => api.delete(`/v1/ai/${assetId}/assistant/conversations/${id}`),
  chat: (assetId: number, message: string, conversationId?: number | null) =>
    api.post(`/v1/ai/${assetId}/assistant/chat`, { message, conversation_id: conversationId || undefined }),
  // Streaming chat (SSE over fetch). Calls onEvent for meta/delta/tool/done/error events.
  chatStream: async (
    assetId: number,
    message: string,
    conversationId: number | null | undefined,
    onEvent: (type: string, data: any) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/v1/ai/${assetId}/assistant/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ message, conversation_id: conversationId || undefined }),
      signal,
    });
    if (!res.ok || !res.body) {
      let msg = `Request failed (${res.status})`;
      let code: string | undefined;
      try { const j = await res.json(); msg = j.message || msg; code = j.code; } catch { /* not JSON */ }
      onEvent('error', { message: msg, code, status: res.status });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        let type = 'message';
        const dataLines: string[] = [];
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event:')) type = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (!dataLines.length) continue;
        try { onEvent(type, JSON.parse(dataLines.join('\n'))); } catch { /* ignore malformed */ }
      }
    }
  },
};

// Mobile app integration: tracking plan wizard + lifecycle analytics
export const appAnalyticsAPI = {
  /** Public ingest origin the SDKs talk to (API URL without the trailing /api). */
  ingestOrigin: () => API_BASE_URL.replace(/\/api\/?$/, ''),
  getMeta: () => api.get('/v1/app-analytics/meta'),
  /** Integration guide (customers only — not a public file). */
  getDocs: () => api.get('/v1/app-analytics/docs'),
  /** Private, signed SDK download links for an asset. */
  getSdkLinks: (assetId: number) => api.get('/v1/app-analytics/sdk-links', { params: { asset_id: assetId } }),
  previewPlan: (answers: any) => api.post('/v1/app-analytics/plan/preview', { answers }),
  getFlow: (assetId: number) => api.get('/v1/app-analytics/flow', { params: { asset_id: assetId } }),
  saveFlow: (assetId: number, answers: any, steps?: any[]) =>
    api.post('/v1/app-analytics/flow', { asset_id: assetId, answers, ...(steps ? { steps } : {}) }),
  updateSettings: (flowId: number, data: any) => api.put(`/v1/app-analytics/flow/${flowId}/settings`, data),
  rotateKey: (flowId: number, revokePrevious = false) =>
    api.post(`/v1/app-analytics/flow/${flowId}/rotate-key`, { revoke_previous: revokePrevious }),
  getVerification: (flowId: number) => api.get(`/v1/app-analytics/flow/${flowId}/verification`),
  getStatus: (assetId: number) => api.get('/v1/app-analytics/status', { params: { asset_id: assetId } }),
  getFilters: (assetId: number) => api.get('/v1/app-analytics/filters', { params: { asset_id: assetId } }),
  getOverview: (params: any) => api.get('/v1/app-analytics/overview', { params }),
  getFunnel: (params: any) => api.get('/v1/app-analytics/funnel', { params }),
  getUsers: (params: any) => api.get('/v1/app-analytics/users', { params }),
  getUserTimeline: (assetId: number, id: number | string) =>
    api.get(`/v1/app-analytics/users/${id}/timeline`, { params: { asset_id: assetId } }),
  getRecentEvents: (assetId: number, limit = 50) => api.get('/v1/app-analytics/events', { params: { asset_id: assetId, limit } }),
};

export default api;
